/* The live lines Settings → Financials shows for the Net Profit Analyzer and Payroll rows:
   this month's net profit (provisional or confirmed) and what is left to close; staff count,
   salary cost and dues. Read from the same ledger and the same /reports/summary the Analyzer
   uses, only while the Financials section is open — never on a timer. */
import { useEffect, useState } from 'react';
import npApi, { R, monthLong, monthSummary, profitChain } from '../../services/netProfit/api';

export default function useNetProfitHeadline(enabled) {
  const [h, setH] = useState({ loading: false, analyzer: null, payroll: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setH((x) => ({ ...x, loading: true }));
    (async () => {
      try {
        const st = await npApi.status();
        if (!alive) return;
        if (st.installed === false) { setH({ loading: false, analyzer: 'Not set up on this server yet', payroll: null }); return; }
        const key = st.focusMonth || st.currentMonth;
        if (!st.firstMonth) {
          setH({ loading: false, analyzer: `${monthLong(key)} · add your fixed costs to see net profit`, payroll: 'No staff added yet' });
          return;
        }
        const [m, staff, summary] = await Promise.all([
          npApi.month(key),
          npApi.staff(key),
          monthSummary(key).catch(() => null),
        ]);
        if (!alive) return;
        const month = m.month;
        const fig = month.locked && month.snapshot ? month.snapshot : (summary ? profitChain(summary, month.totals) : null);
        const left = month.locked ? 0 : month.pending.salariesUnconfirmed + month.pending.billsAwaiting + (month.pending.drafts ? 1 : 0);
        const excl = month.excludesSalaries ? ' (excl. salaries)' : '';
        const analyzer = fig
          ? `${monthLong(key)} · ${month.locked ? 'Confirmed' : 'Provisional'} net profit ${fig.netProfit < 0 ? '−' : ''}${R(Math.abs(fig.netProfit))}${excl}${month.locked ? ' · closed' : (left ? ` · ${left} to confirm before close` : ' · ready to close')}`
          : `${monthLong(key)} · expenses ${R(month.totals.cost)}${excl}${left ? ` · ${left} to confirm` : ''}`;
        const s = staff.staff;
        const active = s.workers.filter((w) => w.workerStatus === 'active').length;
        const payroll = s.masked
          ? `${active} active staff · amounts visible to owner and admin`
          : `${active} active staff · ${R(s.total)} this month${s.duesToStaff > 0 ? ` · dues ${R(s.duesToStaff)}` : ''}${s.advancesTotal > 0 ? ` · advances ${R(s.advancesTotal)}` : ''}`;
        setH({ loading: false, analyzer, payroll });
      } catch (_) {
        if (alive) setH({ loading: false, analyzer: null, payroll: null });
      }
    })();
    return () => { alive = false; };
  }, [enabled]);
  return h;
}
